import { DrawerChartPlayer } from '@/components/DiagramBlock/DrawerChartPlayer';
import DashboardContext from '@/contexts/DashboardContext';
import RealtimeDataContext, { NodeReadingData } from '@/contexts/RealtimeDataContext';
import { BlockDiagram, BlockEdge, BlockNode } from '@/domain/BlockDiagram/Block';
import HistoricalService from '@/domain/Historical/HistoricalService';
import { notification } from 'antd';
import dayjs from 'dayjs';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
	Background,
	BackgroundVariant,
	Connection,
	Edge,
	Node,
	ReactFlow,
	ReactFlowProvider,
	addEdge,
	applyNodeChanges,
	useEdgesState,
	useNodesState
} from 'reactflow';
import 'reactflow/dist/style.css';
import ParameterNode from '../../components/DiagramBlock/Nodes/ParameterNode';
import { Sidebar } from '../../components/DiagramBlock/Sidebar';
import BlockService from '../../domain/BlockDiagram/BlockService';
import DiagramBlockContext from './context';
import './index.scss';

function useQuery() {
	return new URLSearchParams(useLocation().search);
}

let id = 0;
const getId = () => `${id++}`;

const security = JSON.parse(localStorage.getItem('security') || '{}');
const isAdmin = security.role === 'Administrator';

const blockService = new BlockService();
const historicalService = new HistoricalService();

const initialContext = {
	locationId: '',
	infrastructures: [],
};

const nodeTypes = { parameter: ParameterNode };

const DiagramBlockView: React.FC = () => {
	const query = useQuery();
	const isEdit = query.get('isEdit');
	const aditionalProperties = useMemo(() => {
		return {
			...(isEdit
				? {}
				: {
						draggable: false,
						nodesConnectable: false,
						nodesDraggable: false,
						nodesFocusable: false,
						edgesFocusable: false,
					}),
		};
	}, [isEdit]);

	const [contextValue, setContextValue] = useState<any>(initialContext);
	const { value: contextDashboard, setValue: setContextDashboard } = useContext(DashboardContext);
	const navigate = useNavigate();
	const { locationId, blockId } = useParams();
	const reactFlowWrapper = useRef<any>(null);
	const [nodes, setNodes] = useNodesState<Node>([]);
	const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);
	const [reactFlowInstance, setReactFlowInstance] = useState<any>(null);
	const [isEditMode, setIsEditMode] = useState<boolean>(false);
	const [nodeSelect, setNodeSelect] = useState<any>(null);
	const [showPlayback, setShowPlayback] = useState<boolean>(false);
	const [diagramName, setDiagramName] = useState<string>('');
	const [nodeReadings, setNodeReadings] = useState<NodeReadingData[]>([]);
	const [isLoadingReadings, setIsLoadingReadings] = useState<boolean>(false);
	const nodeSelectRef = useRef<any>(null);
	const [currentTime, setCurrentTime] = useState<string | null>(null);
	const [resetTrigger, setResetTrigger] = useState<number>(0);
	const isInitialMount = useRef(true);

	useEffect(() => {
		if (isInitialMount.current) {
			isInitialMount.current = false;
			return;
		}
		
	}, [contextDashboard.selectedLocation, navigate, isEdit]);

	// Handle TimePlayer state changes
	// const handleTimePlayerStateChange = useCallback((state: any) => {
	// 	setCurrentTime(state.currentTime);

	// 	// If TimePlayer has reached the end, trigger reset to start
	// 	if (state.isAtEnd) {
	// 		setTimeout(() => {
	// 			setResetTrigger((prev) => prev + 1);
	// 		}, 100);
	// 	}
	// 	console.log('TimePlayer state changed:', state);
	// }, []);

	// Handle ExampleChartPlayer state changes
	const handleChartPlayerStateChange = useCallback(
		(state: {
			isPlaying: boolean;
			currentTime: string | null;
			progress: number;
			isAtEnd: boolean;
		}) => {
			// Only update if the time actually changed to avoid unnecessary re-renders
			setCurrentTime((prevTime) => {
				if (prevTime !== state.currentTime) {
					return state.currentTime;
				}
				return prevTime;
			});
			
			if (state.isAtEnd) {
				setTimeout(() => {
					setResetTrigger((prev) => prev + 1);
				}, 100);
			}
		},
		[],
	);

	// Function to get node value at specific timestamp
	const getValueAtTime = useCallback(
		(nodeId: string): number | null => {
			if (!currentTime || !showPlayback) return null;

			const nodeData = nodeReadings.find((reading) => reading.nodeId === nodeId);
			if (!nodeData?.readings) return null;

			// Both currentTime and timestamps are now in local timezone
			const targetTimestamp = dayjs(currentTime).valueOf();
			const timestamps = Object.keys(nodeData.readings)
				.map((t) => parseInt(t))
				.sort((a, b) => a - b);

			if (timestamps.length === 0) return null;

			// Find the closest timestamp that is <= targetTimestamp
			let closestTimestamp = timestamps[0];
			for (const timestamp of timestamps) {
				if (timestamp <= targetTimestamp) {
					closestTimestamp = timestamp;
				} else {
					break;
				}
			}

			const foundValue =
				nodeData.readings[closestTimestamp.toString()]?.v !== null
					? nodeData.readings[closestTimestamp.toString()]?.v
					: null;
			return foundValue;
		},
		[currentTime, nodeReadings, showPlayback],
	);

	const onNodesChange = useCallback(
		(changes: any) => {
			setNodes((nds) => applyNodeChanges(changes, nds));
		},
		[setNodes],
	);

	function onChange(event: any, id: any) {
		setNodes((nds) =>
			nds.map((node) => {
				if (node.id !== id) {
					return node;
				}

				const label = event.target.value;

				return {
					...node,
					data: {
						...node.data,
						label,
					},
				};
			}),
		);
	}

	function onChangeParamNode(data: any, id: any) {
		setNodes((nds) =>
			nds.map((node) => {
				if (node.id !== id) {
					return node;
				}

				const label = data.paremeterName;
				const value = data;

				return {
					...node,
					data: {
						...node.data,
						label,
						value,
					},
				};
			}),
		);
	}

	function onAddNode(id: string) {
		const newID = getId();
		setNodes((prev) => {
			const curentNode = prev.find((node) => {
				return node.id == id;
			});

			const newNode = {
				id: newID,
				type: 'parameter',
				position: {
					x: (curentNode?.position.x || 0) + 250,
					y: curentNode?.position.y,
				},
				data: {
					label: `Parameter node`,
					value: {},
					onChange: onChangeParamNode,
					removeNode: removeNode,
					addNode: addNode,
				},
				style: {
					background: getColor('parameter'),
				},
			} as Node;
			return [...prev, newNode];
		});
		const newEdge = {
			id: `reactflow__edge-${id}-${newID}`,
			source: id,
			target: newID,
			type: 'smoothstep',
		};
		setEdges((prev) => {
			return [...prev, newEdge];
		});
	}

	const onConnect = useCallback(
		(params: Edge | Connection) =>
			setEdges((eds) => addEdge({ ...params, type: 'smoothstep' }, eds)),
		[],
	);

	const onDragOver = useCallback(
		(event: { preventDefault: () => void; dataTransfer: { dropEffect: string } }) => {
			event.preventDefault();
			event.dataTransfer.dropEffect = 'move';
		},
		[],
	);

	// Keep nodeSelectRef in sync with nodeSelect state
	useEffect(() => {
		nodeSelectRef.current = nodeSelect;
	}, [nodeSelect]);

	const removeNode = useCallback((id: string) => {
		// Remove the node
		setNodes((prev) => {
			return prev.filter((e) => {
				return e.id != id;
			});
		});
		// Remove edges connected to this node
		setEdges((prev) => {
			return prev.filter((edge) => {
				return edge.source !== id && edge.target !== id;
			});
		});
		// Clear the sidebar/drawer when a node is deleted (using ref for current value)
		if (nodeSelectRef.current?.id === id) {
			setNodeSelect(null);
			setIsEditMode(false);
		}
	}, [setNodes, setEdges]);

	const addNode = (id: string) => {
		onAddNode(id);
	};

	const getColor = (type: any) => {
		switch (type) {
			case 'default': {
				return 'green';
			}
			case 'input': {
				return 'blue';
			}
			case 'output': {
				return 'red';
			}
			default: {
				return 'gray';
			}
		}
	};

	const onDrop = useCallback(
		(event: {
			preventDefault: () => void;
			dataTransfer: { getData: (arg0: string) => any };
			clientX: number;
			clientY: number;
		}) => {
			event.preventDefault();

			const reactFlowBounds = reactFlowWrapper?.current?.getBoundingClientRect();
			const type = event.dataTransfer.getData('application/reactflow');

		
			if (typeof type === 'undefined' || !type) {
				return;
			}

			const position = reactFlowInstance?.project({
				x: event.clientX - reactFlowBounds.left,
				y: event.clientY - reactFlowBounds.top,
			});
			let color = '';
			color = getColor(type);
			let newNode: Node;
			switch (type) {
				case 'parameter':
					newNode = {
						id: getId(),
						type,
						position,
						data: {
							label: `${type} node`,
							value: {},
							editable: isEdit,
							onChange: onChangeParamNode,
							removeNode: removeNode,
							addNode: addNode,
						},
						style: {
							background: color,
						},
					} as Node;
					break;

				default:
					newNode = {
						id: getId(),
						type,
						position,
						data: { label: `${type} node`, value: {}, onChange: onChange },
						style: {
							background: color,
						},
					} as Node;
					break;
			}
			setNodes((nds) => nds.concat(newNode));
		},
		[reactFlowInstance],
	);

	const getBlockDiagram = async () => {
		if (!blockId) {
			navigate(`/`);
			return;
		}
		try {
			setNodes([]);
			setEdges([]);
			const res = await blockService.getBlockDiagram(parseInt(blockId));
			setDiagramName(res.name || '');
			setNodes(
				res.nodes.map((node) => {
					const color = getColor(node.type);
					let newNode: Node;
					switch (node.type) {
						case 'parameter':
							newNode = {
								id: `${node.id}`,
								type: node.type,
								position: node.position,
								data: {
									...node.data,
									editable: isEdit,
									onChange: onChangeParamNode,
									removeNode: removeNode,
									addNode: addNode,
								},
								style: {
									background: color,
								},
							} as Node;
							break;

						default:
							newNode = {
								id: node.id,
								type: node.type,
								position: node.position,
								data: { ...node.data, onChange: onChange },
								style: {
									background: color,
								},
							} as Node;
							break;
					}
					return newNode;
				}),
			);
			setEdges(
				res.edges.map((edge) => {
					return {
						id: `${edge.id}`,
						source: edge.source,
						target: edge.target,
						type: 'smoothstep',
						...(edge.sourceHandle ? { sourceHandle: edge.sourceHandle } : {}),
						...(edge.targetHandle ? { targetHandle: edge.targetHandle } : {}),
					};
				}),
			);
			setTimeout(() => {
				if (reactFlowInstance) reactFlowInstance.fitView();
			}, 200);
			id = res.nodes.length > 0 ? parseInt(res.nodes[res.nodes.length - 1].id) + 1 : 1;
		} catch (error) {
			console.error(error);
			navigate(`/`);
		}
	};

	const saveBlockDiagram = async () => {
		const security = localStorage.getItem('security');
		if (!security) return;
		const organizationId = JSON.parse(security).organizationId;
		const block = {
			id: parseInt(blockId ?? '0'),
			name: 'Test',
			nodes: nodes.map((node) => {
				return {
					id: node.id,
					type: node.type,
					position: { x: node.position.x, y: node.position.y },
					data: node.data,
				} as BlockNode;
			}),
			edges: edges.map((edge) => {
				return {
					id: edge.id,
					source: edge.source,
					target: edge.target,
					...(edge.sourceHandle ? { sourceHandle: edge.sourceHandle } : {}),
					...(edge.targetHandle ? { targetHandle: edge.targetHandle } : {}),
				} as BlockEdge;
			}),
		} as BlockDiagram;
		const response = await blockService.saveBlockDiagram(organizationId, locationId, block);
		if (response) {
			notification.success({
				message: 'Changes saved',
				description: 'Block diagram edited successfully',
			});
		}
	};

	const fetchNodeReadings = async () => {
		if (!contextDashboard.selectedDate[0] || !contextDashboard.selectedDate[1]) {
			console.warn('No dates selected in dashboard context');
			return;
		}

		setIsLoadingReadings(true);
		try {
			// Filter nodes that have parameterId and source in their data.value
			const validNodes = nodes.filter(
				(node) =>
					node.data?.value?.paremeterId &&
					node.data?.value?.serialNumberSource &&
					node.type === 'parameter',
			);

			if (validNodes.length === 0) {
				setNodeReadings([]);
				return;
			}
			const readingsPromises = validNodes.map(async (node) => {
				try {
					const { paremeterId, serialNumberSource } = node.data.value;
					const reading = await historicalService.getReadingsFromDeviceByParams(
						paremeterId,
						serialNumberSource,
						dayjs(contextDashboard.selectedDate[0]).format('YYYY-MM-DD HH:mm:ss'),
						dayjs(contextDashboard.selectedDate[1]).format('YYYY-MM-DD HH:mm:ss'),
						5,
						'minutes',
					);

					// Interpret GMT timestamps as local equivalent moments
					// Service: "17 ago 12:00 GMT" → TimePlayer: "17 ago 12:00 Local" (same visual moment)
					const localReadings = {};
					if (reading?.measurementToolHistoricalValues) {
						Object.keys(reading.measurementToolHistoricalValues).forEach((gmtTimestamp) => {
							// Get GMT moment: "17 ago 12:00 GMT"
							const gmtMoment = dayjs.utc(parseInt(gmtTimestamp));

							// Interpret same date/time as local: "17 ago 12:00 Local"
							const localMoment = dayjs(gmtMoment.format('YYYY-MM-DD HH:mm:ss'));
							const localTimestamp = localMoment.valueOf();

							localReadings[localTimestamp] = reading.measurementToolHistoricalValues[gmtTimestamp];
						});
					}

					return {
						nodeId: node.id,
						nodeName: node.data.label || `Node ${node.id}`,
						paremeterId,
						serialNumberSource,
						readings: localReadings,
						valueUnit: reading?.valueUnit ?? '',
						infrastructureName: reading?.infrastructureName || '',
					};
				} catch (error) {
					console.error(`Error fetching reading for node ${node.id}:`, error);
					return {
						nodeId: node.id,
						nodeName: node.data.label || `Node ${node.id}`,
						parameterId: node.data.value.parameterId,
						source: node.data.value.source,
						reading: null,
						error: error.message,
					};
				}
			});

			const results = await Promise.all(readingsPromises);
			setNodeReadings(results);
		} catch (error) {
			console.error('Error fetching node readings:', error);
			setNodeReadings([]);
		} finally {
			setIsLoadingReadings(false);
		}
	};

	useEffect(() => {
		if (!locationId || !blockId) return;
		setContextValue({ ...contextValue, locationId: locationId });
		getBlockDiagram();
		if (reactFlowInstance) reactFlowInstance.fitView();
	}, [blockId, locationId, contextDashboard.fetchDashboardDataIndex]);

	// Fetch readings when showPlayback is activated
	useEffect(() => {
		if (showPlayback && nodes.length > 0) {
			fetchNodeReadings();
		} else {
			// Clear readings when playback is disabled
			setNodeReadings([]);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [showPlayback, nodes.length, contextDashboard.selectedDate]);

	const realtimeContextValue = {
		realtimeEnabled: showPlayback,
		currentTime,
		nodeReadings,
		getNodeValue: getValueAtTime,
	};

	return (
		<RealtimeDataContext.Provider value={realtimeContextValue}>
			<DiagramBlockContext.Provider
				value={{
					value: contextValue,
					setValue: setContextValue,
				}}
			>
				<div className="containerDiagramBlock">
					<div className="dndflow">
						<ReactFlowProvider>
							<div className="reactflow-wrapper" ref={reactFlowWrapper}>
								<ReactFlow
									nodes={nodes}
									edges={edges}
									onNodesChange={(edgeChanges) => {
										if (edgeChanges[0].type === 'select') {
											const node: any = edgeChanges.find((ec: any) => ec.selected);
											if (node) {
												const ns = nodes.find((n: any) => n.id === node.id);
												setNodeSelect(ns);
												setIsEditMode(true);
												//showEditMode
											} else {
												setNodeSelect(null);
												setIsEditMode(false);
											}
										}
										onNodesChange(edgeChanges);
									}}
									onEdgesChange={onEdgesChange}
									onConnect={onConnect}
									onInit={setReactFlowInstance}
									onDrop={onDrop}
									onDragOver={onDragOver}
									nodeTypes={nodeTypes}
									fitView
									{...aditionalProperties}
								>
									{/* <Controls /> */}
									<Background color="#81818a" gap={16} variant={BackgroundVariant.Dots} />
								</ReactFlow>
							</div>
							{isEdit && (
								<Sidebar
									onSave={saveBlockDiagram}
									isEditMode={isEditMode}
									nodeSelect={nodeSelect}
									locationId={locationId}
									onChangeParamNode={onChangeParamNode}
									closeEditMode={() => {
										setIsEditMode(false);
									}}
									nodes={nodes}
								/>
							)}
						</ReactFlowProvider>

						{/* Toggle Switch for Playback */}
						{/*!isEdit && (
							<div
								className={`playback-toggle ${showPlayback && !isLoadingReadings ? 'with-timeplayer' : ''}`}
							>
								<Switch
									checked={showPlayback}
									checkedChildren="Hide Playback"
									unCheckedChildren="Show Playback"
									onChange={setShowPlayback}
									className="playback-switch"
									loading={isLoadingReadings}
									disabled={isLoadingReadings}
								/>
								{isLoadingReadings && <span className="loading-message">Loading readings...</span>}
							</div>
						) */}

						{!isEdit && (
							<DrawerChartPlayer
								chartStartDate={contextDashboard.selectedDate[0]}
								chartEndDate={contextDashboard.selectedDate[1]}
								chartNodeData={nodeReadings}
								drawerTitle={diagramName}
								isChartDataLoading={isLoadingReadings}
								isDrawerVisible={showPlayback}
								onDrawerVisibilityChange={setShowPlayback}
								onPlayerStateChange={handleChartPlayerStateChange}
							/>
						)}

						{/* TimePlayer Component */}
						{showPlayback && !isLoadingReadings && (
							<div className="time-player-wrapper">
								{/* <TimePlayer
									title={diagramName}
									startDate={contextDashboard.selectedDate[0]}
									endDate={contextDashboard.selectedDate[1]}
									step={5}
									stepsBySecond={5}
									resetTrigger={resetTrigger}
									events={nodeReadings
										.filter((reading) => reading.reading && !reading.error)
										.map((reading, index) => ({
											time: contextDashboard.selectedDate[0], // Initial time, could be enhanced
											label: `${reading.nodeName} - ${reading.parameterId}`,
											events: [
												{
													infraName: reading.nodeName,
													mod_value: reading.reading?.sourceId || 'N/A',
												},
											],
										}))}
									onStateChange={handleTimePlayerStateChange}
								/> */}
								{/* <DrawerChartPlayer
									startDate={contextDashboard.selectedDate[0]}
									endDate={contextDashboard.selectedDate[1]}
									dataChart={nodeReadings}
									title={diagramName}
									onStateChange={handleChartPlayerStateChange}
								/> */}
							</div>
						)}

						{/* Diagram Name Display */}
						{diagramName && (!showPlayback || isLoadingReadings) && (
							<div className="diagram-name-display">{diagramName}</div>
						)}
					</div>
				</div>
			</DiagramBlockContext.Provider>
		</RealtimeDataContext.Provider>
	);
};

export default DiagramBlockView;
